import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React from "react";
import { useSelector } from "react-redux";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { Icon } from "../schedule/ui";
import ProductFormDrawer from "./ProductFormDrawer";
import { describe, FREE_TRIAL_NAME, KIND, productKind, saleStatus } from "./productUtils";
import "../../../styles/onboarding.css";

/**
 * My Products (new design). Route /products (same route as before).
 * Loads all products at once (products/list?getall=true), groups them on the front end:
 * credit passes, subscriptions and free credit passes (credit passes at €0).
 * Uses the same endpoints as the old page: products/add, products/update/:id, DELETE products/:id.
 */

const TICKET = <path d="M3 9a2 2 0 0 0 0 6v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0 0-6V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />;
const ICONS = {
  pass: <>{TICKET}<path d="M13 5v2M13 17v2M13 11v2" /></>,
  sub: <><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></>,
  free: <><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1.5S9 8 12 8c3 0 6-1 6-1.5S14 4 12 8z" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
  eyeOff: <><path d="M10.7 5.1A10.8 10.8 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.2 3.2M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" /><path d="m2 2 20 20" /></>,
  trash: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />,
  warn: <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>,
};
const DOTS = <><circle cx="12" cy="5" r="1.6" fill="currentColor" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><circle cx="12" cy="19" r="1.6" fill="currentColor" /></>;
const ICON_TONE = { pass: "bg-[#F3E9FF] text-[#6A00BA]", sub: "bg-[#FFE8EB] text-[#B4232F]", free: "bg-[#E7F0FF] text-[#1D4ED8]" };

const FILTERS = [
  ["all", "All"],
  ["pass", "Credit passes"],
  ["sub", "Subscriptions"],
  ["free", "Free credits"],
];

export default function ProductsPage({ title = "My Products" }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const dashboardReduxState = useSelector(dashboardState);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [form, setForm] = React.useState({ open: false, kind: "pass", product: null });
  const [deleting, setDeleting] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoadError(false);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=true` });
      const data = res?.status === 200 && res?.data?.statusCode === 200 ? res.data.data : null;
      if (Array.isArray(data)) setProducts(data.filter((p) => p.productName !== FREE_TRIAL_NAME));
      else setLoadError(true);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load, dashboardReduxState.updateSessionComp]);

  const counts = React.useMemo(() => {
    const c = { all: products.length, pass: 0, sub: 0, free: 0 };
    products.forEach((p) => c[productKind(p)]++);
    return c;
  }, [products]);
  const visible = filter === "all" ? products : products.filter((p) => productKind(p) === filter);
  const onSale = products.filter((p) => productKind(p) !== "free" && p.availableForSale !== false).length;

  const openNew = (kind) => setForm({ open: true, kind, product: null });
  const openEdit = (p) => setForm({ open: true, kind: productKind(p), product: p });

  const setSale = async (p, value) => {
    setBusyId(p._id);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForClasses("")}products/update/${p._id}`, data: { availableForSale: value } });
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        setProducts((list) => list.map((x) => (x._id === p._id ? { ...x, availableForSale: value } : x)));
        addSuccessToast(value ? `${p.productName} is on sale` : `${p.productName} hidden from the app and webshop`);
        return true;
      }
      addErrorToast(typeof res?.data?.message === "string" && res.data.message ? res.data.message : "Couldn't update the product.");
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusyId(null);
    }
    return false;
  };

  const remove = async () => {
    const p = deleting;
    setBusyId(p._id);
    try {
      const res = await axiosInstance.delete(`${getUrlForClasses("")}products/${p._id}`, {});
      if (res?.data?.statusCode === 200) {
        setProducts((list) => list.filter((x) => x._id !== p._id));
        addSuccessToast(`${p.productName} deleted`);
        setDeleting(null);
      } else addErrorToast(typeof res?.data?.message === "string" && res.data.message ? res.data.message : "Couldn't delete the product.");
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const noProductsAtAll = !loading && !loadError && products.length === 0;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
              <p className="mt-1 max-w-2xl text-sm text-gray-600 sm:text-base">Credit passes and subscriptions members buy to book your classes. Products on sale show in the FitClub app and your webshop.</p>
            </div>
            <NewProductMenu onPick={openNew} />
          </div>

          {noProductsAtAll ? (
            <EmptyAll onPass={() => openNew("pass")} onSub={() => openNew("sub")} />
          ) : (
            <>
              {/* Toolbar */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div role="tablist" aria-label="Filter products" className="flex gap-1 overflow-x-auto rounded-2xl bg-[#F3EEF5] p-1">
                  {FILTERS.map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={filter === k}
                      onClick={() => setFilter(k)}
                      className={classNames("h-9 shrink-0 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold", filter === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
                    >
                      {l} <span className="opacity-60">{loading ? "" : counts[k]}</span>
                    </button>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-600 sm:ml-auto">{loading ? "" : `${onSale} on sale`}</span>
              </div>

              {loading ? (
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse overflow-hidden rounded-3xl border border-gray-200 bg-white">
                      <div className="h-1.5 bg-[#EDE5F0]" />
                      <div className="flex flex-col gap-3 p-5">
                        <span className="h-5 w-24 rounded-full bg-[#F3EEF5]" />
                        <span className="h-5 w-40 rounded-full bg-[#EDE5F0]" />
                        <span className="h-3 w-56 max-w-full rounded-full bg-[#F3EEF5]" />
                        <span className="mt-1 h-7 w-24 rounded-full bg-[#EDE5F0]" />
                        <span className="mt-2 h-3 w-32 rounded-full bg-[#F3EEF5]" />
                        <span className="h-3 w-28 rounded-full bg-[#F3EEF5]" />
                        <span className="h-3 w-36 rounded-full bg-[#F3EEF5]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : loadError ? (
                <div className="mt-5 rounded-3xl border border-gray-200 bg-white px-6 py-12 text-center">
                  <p className="text-sm font-bold text-gray-700">Couldn't load your products.</p>
                  <button type="button" onClick={() => { setLoading(true); load(); }} className="mt-3 rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
                    Try again
                  </button>
                </div>
              ) : visible.length === 0 ? (
                <div className="mt-5 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-12 text-center">
                  <p className="text-base font-black text-gray-900">Nothing in this list yet</p>
                  <p className="mt-1 max-w-md text-sm text-gray-600">
                    {filter === "free" ? "A free credit pass holds credits you give to members or import from another system." : "Add one with New product."}
                  </p>
                  <button type="button" onClick={() => openNew(filter)} className="mt-4 rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                    {filter === "pass" ? "New credit pass" : filter === "sub" ? "New subscription" : "New free credit pass"}
                  </button>
                </div>
              ) : (
                <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {visible.map((p) => (
                    <ProductCard
                      key={p._id}
                      product={p}
                      busy={busyId === p._id}
                      onEdit={() => openEdit(p)}
                      onToggleSale={() => setSale(p, p.availableForSale === false)}
                      onDelete={() => setDeleting(p)}
                    />
                  ))}
                </ul>
              )}
            </>
          )}

          <ProductFormDrawer open={form.open} kind={form.kind} product={form.product} onClose={() => setForm((f) => ({ ...f, open: false }))} onSaved={load} />

          <DeleteDialog
            product={deleting}
            busy={!!deleting && busyId === deleting._id}
            onClose={() => setDeleting(null)}
            onDelete={remove}
            onHide={async () => {
              if (await setSale(deleting, false)) setDeleting(null);
            }}
          />
        </div>
      }
    />
  );
}

function NewProductMenu({ onPick }) {
  const items = [
    { kind: "pass", label: "Credit pass", sub: "A bundle of credits, paid once. e.g. 10 credits for €120" },
    { kind: "sub", label: "Subscription", sub: "Renews automatically. e.g. unlimited credits for €79 a month" },
    { kind: "free", label: "Free credit pass", sub: "€0, never sold. Holds credits you give or import" },
  ];
  return (
    <Menu as="div" className="relative">
      <MenuButton className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-base font-extrabold text-white shadow-[0_10px_24px_rgba(72,27,58,0.25)] hover:bg-light-brown-coffee sm:w-auto">
        <Icon className="h-5 w-5" d={<path d="M12 5v14M5 12h14" />} />
        New product
        <Icon className="h-4 w-4" d={<path d="m6 9 6 6 6-6" />} />
      </MenuButton>
      <MenuItems anchor="bottom end" className="z-[60] mt-2 w-80 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
        {items.map((it) => (
          <MenuItem key={it.kind}>
            <button type="button" onClick={() => onPick(it.kind)} className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left data-[focus]:bg-[#F7F4F8]">
              <span className={classNames("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", ICON_TONE[it.kind])}>
                <Icon className="h-[18px] w-[18px]" d={ICONS[it.kind]} />
              </span>
              <span>
                <span className="block text-sm font-extrabold text-gray-900">{it.label}</span>
                <span className="mt-0.5 block text-xs font-semibold text-gray-500">{it.sub}</span>
              </span>
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

function ProductCard({ product: p, busy, onEdit, onToggleSale, onDelete }) {
  const d = describe(p);
  const K = KIND[d.kind];
  const sale = saleStatus(p);
  const canToggle = d.kind !== "free" || p.availableForSale !== false; // free passes can only be taken off sale
  const dim = sale.key === "hidden";
  const name = p.productName ? p.productName.charAt(0).toUpperCase() + p.productName.slice(1) : "Product";
  return (
    <li className={classNames("flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.05)] transition-opacity", dim && "opacity-75", busy && "pointer-events-none opacity-60")}>
      <div className={classNames("h-1.5", K.band)} />
      <div className="flex flex-1 flex-col gap-3 px-5 pb-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <span className={classNames("rounded-full px-2.5 py-1 text-xs font-extrabold", K.chip)}>{K.label}</span>
          <Menu as="div" className="relative -mr-2 -mt-1.5">
            <MenuButton aria-label={`Options for ${name}`} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900">
              <Icon d={DOTS} />
            </MenuButton>
            <MenuItems anchor="bottom end" className="z-[60] mt-1 w-60 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
              <MenuItem>
                <button type="button" onClick={onEdit} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                  <Icon className="h-[18px] w-[18px] text-gray-500" d={ICONS.edit} /> Edit
                </button>
              </MenuItem>
              {canToggle && (
                <MenuItem>
                  <button type="button" onClick={onToggleSale} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                    <Icon className="h-[18px] w-[18px] text-gray-500" d={p.availableForSale === false ? ICONS.eye : ICONS.eyeOff} />
                    {p.availableForSale === false ? "Put on sale" : "Hide from app & webshop"}
                  </button>
                </MenuItem>
              )}
              <MenuItem>
                <button type="button" onClick={onDelete} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6]">
                  <Icon className="h-[18px] w-[18px]" d={ICONS.trash} /> Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
        <div>
          <h2 className="break-words text-lg font-black tracking-tight text-gray-900">{name}</h2>
          <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm leading-snug text-gray-500">{p.productDesc || "No description yet."}</p>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-black tracking-tight text-gray-900">{d.priceMain}</span>
          <span className="text-sm font-bold text-gray-500">{d.priceSub}</span>
        </div>
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
          <Fact icon={TICKET}>{d.credits}</Fact>
          <Fact icon={ICONS.clock}>{d.validity}</Fact>
          <Fact icon={ICONS.calendar}>{d.classes}</Fact>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
        <span className={classNames("inline-flex items-center gap-2 text-sm font-extrabold", sale.text)}>
          <span className={classNames("h-2 w-2 rounded-full", sale.dot)} />
          {sale.label}
        </span>
        <button type="button" onClick={onEdit} className="rounded-lg px-2 py-1 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
          Edit
        </button>
      </div>
    </li>
  );
}

function Fact({ icon, children }) {
  return (
    <span className="flex items-start gap-2 text-sm font-bold text-gray-700">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-portland-orange" d={icon} />
      <span className="min-w-0 break-words">{children}</span>
    </span>
  );
}

function EmptyAll({ onPass, onSub }) {
  return (
    <div className="mt-8 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-14 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
        <Icon className="h-8 w-8" d={ICONS.pass} />
      </span>
      <h2 className="mt-5 text-xl font-black text-gray-900">No products yet</h2>
      <p className="mt-2 max-w-md text-sm text-gray-600 sm:text-base">Members need a pass or a subscription to book. Start with a credit pass, for example 10 credits for €120.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button type="button" onClick={onSub} className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
          New subscription
        </button>
        <button type="button" onClick={onPass} className="rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
          New credit pass
        </button>
      </div>
    </div>
  );
}

/** Delete confirm with "Hide instead" for products that are still on sale. */
function DeleteDialog({ product, busy, onClose, onDelete, onHide }) {
  const [last, setLast] = React.useState(product);
  React.useEffect(() => {
    if (product) setLast(product);
  }, [product]);
  const p = product || last;
  const canHide = p && p.availableForSale !== false;
  return (
    <Dialog open={!!product} onClose={() => !busy && onClose()} className="relative z-[60]">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
        <DialogPanel transition className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl transition duration-200 data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:rounded-3xl">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFE8EB] text-[#B4232F]">
              <Icon d={ICONS.warn} />
            </span>
            <div>
              <DialogTitle className="text-lg font-black text-gray-900">Delete {p?.productName || "this product"}?</DialogTitle>
              <p className="mt-1.5 text-sm text-gray-600">
                It disappears from the app and your webshop. Members who already bought it keep it.
                {canHide ? " If you only want to stop selling it for now, hide it instead." : ""}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" disabled={busy} onClick={onClose} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
              Keep it
            </button>
            {canHide && (
              <button type="button" disabled={busy} onClick={onHide} className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
                Hide instead
              </button>
            )}
            <button type="button" disabled={busy} onClick={onDelete} className="flex items-center justify-center gap-2 rounded-2xl bg-[#B4232F] px-5 py-3 text-sm font-extrabold text-white hover:bg-[#9a1d28] disabled:opacity-60">
              {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              Delete
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
